import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { ForecastType, ResultType } from '@/tools/signalForecast/signalForecast.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type {
  BaseToolPersistedState,
  ParameterDefinitions,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
  ToolRehydratePayload,
} from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export const DEFAULT_DURATION = { value: 1, units: 'day' };

export interface SignalForecastState extends BaseToolPersistedState {
  targetSignal: ToolItem | undefined;
  runCycleCondition: ToolItem | undefined;
  forecastSignal: ToolItem | undefined;
  trainingDuration: ValueWithUnitsItem;
  blendWindow: ValueWithUnitsItem;
  period: ValueWithUnitsItem;
  forecastDuration: ValueWithUnitsItem;
  griddingPeriod: ValueWithUnitsItem;
  forecastType: ForecastType;
  resultType: ResultType;
  forceThroughLastSample: boolean;
  resampleTargetSignal: boolean;
  isSeasonal: boolean;
  color: string;
}

export interface SignalForecastActions {
  setTargetSignal: (targetSignal: ToolItem | null | undefined) => void;
  setRunCycleCondition: (runCycleCondition: ToolItem | null | undefined) => void;
  setForecastSignal: (forecastSignal: ToolItem | null | undefined) => void;
  setTrainingDuration: (trainingDuration: ValueWithUnitsItem) => void;
  setForecastDuration: (forecastDuration: ValueWithUnitsItem) => void;
  setBlendWindow: (blendWindow: ValueWithUnitsItem) => void;
  setForecastType: (forecastType: ForecastType) => void;
  setPeriod: (period: ValueWithUnitsItem) => void;
  setGriddingPeriod: (griddingPeriod: ValueWithUnitsItem) => void;
  setResampleTargetSignal: (resampleTargetSignal: boolean) => void;
  setColor: (color: string) => void;
  setForceThroughLastSample: (forceThroughLastSample: boolean) => void;
  setIsSeasonal: (isSeasonal: boolean) => void;
  setResultType: (resultType: ResultType) => void;
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (advancedParametersCollapsed: boolean) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: () => void;
}

export type SignalForecastDehydratedState = SignalForecastState;

const parameterDefinitions: ParameterDefinitions<'forecastSignal' | 'runCycleCondition' | 'targetSignal'> = {
  forecastSignal: { predicate: ['name', 'signal2'] },
  runCycleCondition: { predicate: ['name', 'condition'] },
  targetSignal: {
    predicate: (parameter) => {
      const name = parameter.name ?? '';
      return name !== 'condition' && name !== 'signal2';
    },
  },
};

const initialState: SignalForecastState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  targetSignal: undefined,
  runCycleCondition: undefined,
  forecastSignal: undefined,
  trainingDuration: { value: 7, units: 'days' },
  blendWindow: { value: 0, units: 'min' },
  period: { ...DEFAULT_DURATION },
  forecastDuration: { ...DEFAULT_DURATION },
  griddingPeriod: { ...DEFAULT_DURATION },
  forecastType: ForecastType.LINEAR,
  resultType: ResultType.FORECAST,
  forceThroughLastSample: false,
  resampleTargetSignal: false,
  isSeasonal: false,
  color: '',
});

const signalForecastConfigParamsSelector = createSelector(
  [(state: SignalForecastState) => state],
  (state): ToolConfigParams =>
    getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.SIGNAL_FORECAST,
      parameterDefinitionKeys: Object.keys(parameterDefinitions),
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    }),
);

export const dehydrateSignalForecastState = (state: SignalForecastState): SignalForecastDehydratedState => state;

const signalForecastActionsFactory: ActionsFactory<SignalForecastState, SignalForecastActions> = (
  set,
  _get,
  resetState,
) => ({
  setTargetSignal: (targetSignal) =>
    set((draft) => {
      draft.targetSignal = sanitizeToolItem(targetSignal) ?? undefined;
    }),

  setRunCycleCondition: (runCycleCondition) =>
    set((draft) => {
      draft.runCycleCondition = sanitizeToolItem(runCycleCondition) ?? undefined;
    }),

  setForecastSignal: (forecastSignal) =>
    set((draft) => {
      draft.forecastSignal = sanitizeToolItem(forecastSignal) ?? undefined;
    }),

  setTrainingDuration: (trainingDuration) =>
    set((draft) => {
      draft.trainingDuration = trainingDuration;
    }),

  setForecastDuration: (forecastDuration) =>
    set((draft) => {
      draft.forecastDuration = forecastDuration;
    }),

  setBlendWindow: (blendWindow) =>
    set((draft) => {
      draft.blendWindow = blendWindow;
    }),

  setForecastType: (forecastType) =>
    set((draft) => {
      draft.forecastType = forecastType;
    }),

  setPeriod: (period) =>
    set((draft) => {
      draft.period = period;
    }),

  setGriddingPeriod: (griddingPeriod) =>
    set((draft) => {
      draft.griddingPeriod = griddingPeriod;
    }),

  setResampleTargetSignal: (resampleTargetSignal) =>
    set((draft) => {
      draft.resampleTargetSignal = resampleTargetSignal;
    }),

  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),

  setForceThroughLastSample: (forceThroughLastSample) =>
    set((draft) => {
      draft.forceThroughLastSample = forceThroughLastSample;
    }),

  setIsSeasonal: (isSeasonal) =>
    set((draft) => {
      draft.isSeasonal = isSeasonal;
    }),

  setResultType: (resultType) =>
    set((draft) => {
      draft.resultType = resultType;
    }),

  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (advancedParametersCollapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = advancedParametersCollapsed;
    }),

  rehydrateForEdit: (payload) => {
    resetState();
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });

      Object.assign(draft, rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });
    });
  },

  reset: resetState,
});

const STORE_NAME = 'SignalForecast';

const [SignalForecastStoreProvider, useSignalForecastStoreInstance, useSignalForecastStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  signalForecastActionsFactory,
);

export { SignalForecastStoreProvider };

export const useSignalForecastId = () => useSignalForecastStoreSelector((state) => state.id);
export const useSignalForecastName = () => useSignalForecastStoreSelector((state) => state.name);
export const useSignalForecastOriginalParameters = () =>
  useSignalForecastStoreSelector((state) => state.originalParameters);
export const useSignalForecastAdvancedParametersCollapsed = () =>
  useSignalForecastStoreSelector((state) => state.advancedParametersCollapsed);
export const useSignalForecastTargetSignal = () => useSignalForecastStoreSelector((state) => state.targetSignal);
export const useSignalForecastRunCycleCondition = () =>
  useSignalForecastStoreSelector((state) => state.runCycleCondition);
export const useSignalForecastForecastSignal = () => useSignalForecastStoreSelector((state) => state.forecastSignal);
export const useSignalForecastTrainingDuration = () =>
  useSignalForecastStoreSelector((state) => state.trainingDuration);
export const useSignalForecastBlendWindow = () => useSignalForecastStoreSelector((state) => state.blendWindow);
export const useSignalForecastPeriod = () => useSignalForecastStoreSelector((state) => state.period);
export const useSignalForecastForecastDuration = () =>
  useSignalForecastStoreSelector((state) => state.forecastDuration);
export const useSignalForecastGriddingPeriod = () => useSignalForecastStoreSelector((state) => state.griddingPeriod);
export const useSignalForecastType = () => useSignalForecastStoreSelector((state) => state.forecastType);
export const useSignalForecastResultType = () => useSignalForecastStoreSelector((state) => state.resultType);
export const useSignalForecastForceThroughLastSample = () =>
  useSignalForecastStoreSelector((state) => state.forceThroughLastSample);
export const useSignalForecastResampleTargetSignal = () =>
  useSignalForecastStoreSelector((state) => state.resampleTargetSignal);
export const useSignalForecastIsSeasonal = () => useSignalForecastStoreSelector((state) => state.isSeasonal);
export const useSignalForecastColor = () => useSignalForecastStoreSelector((state) => state.color);
export const useSignalForecastConfigParams = () => useSignalForecastStoreSelector(signalForecastConfigParamsSelector);

export const useSignalForecastActions = () => useSignalForecastStoreInstance().actions;

export function createTestSignalForecastStore(overrides?: Partial<SignalForecastState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestSignalForecastStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, signalForecastActionsFactory);
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateSignalForecastStore() {
  return createStoreInstance<SignalForecastState, SignalForecastActions>(initialState, signalForecastActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistSignalForecastStore = () => {
  const register = useRegisterPersistedStore();
  const store = useSignalForecastStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqSignalForecastStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateSignalForecastState,
    });
  }, [register, store]);
};
